import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Formik, Form, FormikHelpers } from "formik";
import * as Yup from "yup";
import {
  Card,
  CardContent,
  Grid,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  // Box as MuiBox,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Add, Delete, Edit, Save, ArrowBack, Visibility, Lock } from "@mui/icons-material";

// Argon Dashboard 2 MUI components
import Box from "themeComponents/Box";
import Typography from "themeComponents/Typography";
import Button from "themeComponents/Button";
import Input from "themeComponents/Input";
import FormikInput from "components/formikInput";
import Loader from "components/loader";
import DashboardLayout from "themeComponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "themeComponents/Navbars/DashboardNavbar";
import Footer from "themeComponents/Footer";
import { AppContext } from "context/appContext";
import { assessmentApi } from "configs/api";
import { useCourse } from "hooks/useCourse";
import useAuth from "hooks/useAuth";
import CourseCompletionScreen from "layouts/course/CourseCompletionScreen";

interface Question {
  id?: string;
  question: string;
  type: "mcq";
  order: number;
  points: number;
  options: Option[];
}

interface Option {
  id?: string;
  optionText: string;
  isCorrect: boolean;
  order: number;
}

interface Assessment {
  id?: string;
  courseID: string;
  moduleID?: string | null;
  title: string;
  description?: string;
  type: "mcq";
  passingScore: number;
  timeLimit?: number;
  order: number;

  unlocksModuleID?: string | null;
  questions: Question[];
}

const normalizeAssessmentResponse = (payload: any): Assessment | null => {
  const source = payload?.data ?? payload;
  if (!source || typeof source !== "object") {
    return null;
  }

  return {
    id: source.id,
    courseID: source.courseID || source.courseId || "",
    moduleID: source.moduleID ?? null,
    title: source.title || "",
    description: source.description || "",
    type: "mcq",
    passingScore: source.passingScore ?? 70,
    timeLimit: source.timeLimit ?? 0,
    order: source.order ?? 0,
    unlocksModuleID: source.unlocksModuleID ?? null,
    questions: (source.questions || []).map((q: any, qIndex: number) => ({
      id: q.id,
      question: q.question || "",
      type: "mcq",
      order: q.order ?? qIndex,
      points: q.points ?? 1,
      options: (q.options || []).map((opt: any, optIndex: number) => ({
        id: opt.id,
        optionText: opt.optionText ?? opt.text ?? "",
        isCorrect: opt.isCorrect ?? false,
        order: opt.order ?? optIndex,
      })),
    })),
  };
};

function AssessmentBuilder(): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ courseId?: string; courseID?: string; assessmentID?: string }>();
  const courseId = params.courseId || params.courseID;
  const assessmentID = params.assessmentID;
  const { showAlert } = useContext(AppContext);
  const { currentCourse, getCourse } = useCourse();
  const { user } = useAuth();
  const [courseModules, setCourseModules] = useState<Array<{ id?: string; title: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [openQuestionDialog, setOpenQuestionDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [editingQuestionIndex, setEditingQuestionIndex] = useState<number | null>(null);
  const [completionContext, setCompletionContext] = useState<{
    enrollmentId: string;
    assessmentScore: number;
    userId: string;
    userName: string;
  } | null>(null);
  const isReadOnly = (location.state as any)?.mode === "view";
  
  // Determine where to navigate back to
  const getBackPath = () => {
    // Check if we came from the builder (via location state or referrer)
    const fromBuilder = (location.state as any)?.fromBuilder || 
                        document.referrer.includes('/builder') ||
                        document.referrer.includes('/edit');
    if (fromBuilder && courseId) {
      // Try to get the course ID from the builder path
      const builderMatch = document.referrer.match(/\/courses\/([^\/]+)\/(builder|edit)/);
      if (builderMatch) {
        return `/courses/${builderMatch[1]}/builder`;
      }
      return `/courses/${courseId}/builder`;
    }
    // Default: go to course detail
    return courseId ? `/courses/${courseId}` : '/courses';
  };
  
  const handleBack = () => {
    // If we have history, go back, otherwise navigate to course
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(getBackPath());
    }
  };

  // Initialize assessment if creating new
  useEffect(() => {
    if (!assessmentID && courseId && !assessment) {
      setAssessment({
        courseID: courseId,
        title: "",
        type: "mcq",
        passingScore: 70,
        order: 0,
        questions: [],
      });
    }
  }, [assessmentID, courseId, assessment]);

  useEffect(() => {
    if (courseId) {
      getCourse(courseId)
        .then((payload: any) => {
          const source = payload?.data ?? payload;
          const modules = Array.isArray(source?.modules) ? source.modules : [];
          setCourseModules(modules);
        })
        .catch(() => {
          setCourseModules([]);
        });
    }
    if (assessmentID) {
      fetchAssessment();
    }
  }, [courseId, assessmentID, getCourse]);

  const availableModules = courseModules.length > 0 ? courseModules : (currentCourse?.modules || []);

  const getModuleValue = (module: any, index: number) => module?.id || index.toString();

  const fetchAssessment = async () => {
    try {
      const response = await assessmentApi.get(`/api/assessments/${assessmentID}`);
      const normalized = normalizeAssessmentResponse(response.data);
      if (normalized) {
        setAssessment(normalized);
      } else {
        throw new Error("Invalid assessment response");
      }
    } catch (error: any) {
      showAlert(error.response?.data?.message || "Failed to fetch assessment", "error", "Error");
    }
  };

  const handleAddQuestion = () => {
    // Ensure assessment is initialized
    if (!assessment) {
      const newAssessment: Assessment = {
        courseID: courseId || "",
        title: "",
        type: "mcq",
        passingScore: 70,
        order: 0,
        questions: [],
      };
      setAssessment(newAssessment);
      return;
    }
    setEditingQuestion(null);
    setEditingQuestionIndex(null);
    setOpenQuestionDialog(true);
  };

  const handleEditQuestion = (index: number) => {
    if (assessment) {
      setEditingQuestion(assessment.questions[index]);
      setEditingQuestionIndex(index);
      setOpenQuestionDialog(true);
    }
  };

  const handleDeleteQuestion = (index: number) => {
    if (assessment) {
      const newQuestions = assessment.questions.filter((_, i) => i !== index);
      setAssessment({ ...assessment, questions: newQuestions.map((q, i) => ({ ...q, order: i })) });
    }
  };

  const handleSaveQuestion = (questionData: Partial<Question>) => {
    if (!assessment) {
      // Initialize assessment if it doesn't exist
      const newAssessment: Assessment = {
        courseID: courseId || "",
        title: "",
        type: "mcq",
        passingScore: 70,
        order: 0,
        questions: [],
      };
      setAssessment(newAssessment);
      return;
    }

    const newQuestion: Question = {
      question: questionData.question || "",
      type: "mcq",
      order: editingQuestionIndex !== null ? editingQuestionIndex : (assessment.questions?.length || 0),
      points: questionData.points || 1,
      options: questionData.options || [],
    };

    const currentQuestions = assessment.questions || [];
    
    if (editingQuestionIndex !== null && currentQuestions[editingQuestionIndex]) {
      const updated = [...currentQuestions];
      updated[editingQuestionIndex] = { ...updated[editingQuestionIndex], ...newQuestion };
      setAssessment({ ...assessment, questions: updated });
    } else {
      setAssessment({ ...assessment, questions: [...currentQuestions, newQuestion] });
    }

    setOpenQuestionDialog(false);
    setEditingQuestion(null);
    setEditingQuestionIndex(null);
  };

  const handleSaveAssessment = async (values: Partial<Assessment>) => {
    if (!assessment || !courseId) return;
    setLoading(true);
    try {
      // Map frontend values to backend DTO (PascalCase)
      let assessmentData: any = {
        CourseID: courseId,
        Title: values.title,
        Description: values.description,
        Type: "mcq",
        PassingScore: values.passingScore,
        TimeLimitMinutes: values.timeLimit || null,
        Order: 0,
        ModuleID: values.moduleID === "" ? null : values.moduleID,
        UnlocksModuleID: values.unlocksModuleID === "" ? null : values.unlocksModuleID,
      };
      let savedAssessmentId = assessmentID;
      if (assessmentID) {
        // Update existing assessment
        await assessmentApi.put(`/api/assessments/${assessmentID}`, assessmentData);
        showAlert("Assessment updated successfully!", "success", "Success");
      } else {
        // Create new assessment
        const response = await assessmentApi.post("/api/assessments", assessmentData);
        if (response.data.status) {
          savedAssessmentId = response.data.data.id;
          showAlert("Assessment created successfully! Saving questions...", "success", "Success");
        } else {
          throw new Error("Failed to create assessment");
        }
      }

      // Now save questions separately
      if (savedAssessmentId && assessment?.questions && assessment.questions?.length > 0) {
        let savedCount = 0;
        let failedCount = 0;
        
        // Save each question with its options
        for (const question of assessment.questions) {
          try {
            const questionPayload = {
              Question: question.question,
              Type: question.type || "mcq",
              Order: question.order,
              Points: question.points || 1,
              Options: (question.options || []).map((option, index) => ({
                Text: option.optionText,
                IsCorrect: option.isCorrect,
                Order: option.order ?? index,
              })),
            };

            if (question.id && assessmentID) {
              // Update existing question
              const updateResponse = await assessmentApi.put(
                `/api/assessments/questions/${question.id}`,
                questionPayload
              );
              if (updateResponse.data.status) {
                savedCount++;
              } else {
                failedCount++;
                console.error("Failed to update question:", question.question);
              }
            } else {
              // Create new question
              const createPayload = {
                AssessmentID: savedAssessmentId,
                ...questionPayload,
              };
              const questionResponse = await assessmentApi.post("/api/assessments/questions", createPayload);
              if (questionResponse.data.status && questionResponse.data.data?.id) {
                savedCount++;
              } else {
                failedCount++;
                console.error("Failed to save question:", question.question);
              }
            }
          } catch (questionError: any) {
            failedCount++;
            console.error("Error saving question:", questionError);
            showAlert(
              `Failed to save question: ${question.question}`,
              "warning",
              "Warning"
            );
          }
        }
        
        if (savedCount > 0) {
          showAlert(
            `Successfully saved ${savedCount} question${savedCount !== 1 ? 's' : ''}${failedCount > 0 ? ` (${failedCount} failed)` : ''}`,
            failedCount > 0 ? "warning" : "success",
            "Questions Saved"
          );
        }
      } else if (savedAssessmentId && (!assessment?.questions || assessment.questions?.length === 0)) {
        showAlert("Assessment saved. You can add questions later.", "info", "Info");
      }

      const completionRequest = (location.state as any)?.completionContext;
      if (
        completionRequest?.enrollmentId &&
        courseId &&
        (completionRequest?.userId || user?._id || user?.id)
      ) {
        setCompletionContext({
          enrollmentId: completionRequest.enrollmentId,
          assessmentScore: completionRequest.assessmentScore ?? 100,
          userId: completionRequest.userId || user?._id || user?.id,
          userName:
            completionRequest.userName ||
            [user?.firstname, user?.lastname].filter(Boolean).join(" ") ||
            user?.name ||
            "Learner",
        });
        return;
      }

      // Navigate after saving
      navigate(`/courses/${courseId}/assessments/list`, { replace: true });
    } catch (error: any) {
      showAlert(error.response?.data?.message || "Failed to save assessment", "error", "Error");
    } finally {
      setLoading(false);
    }
  };

  if (!courseId) {
    return <Loader loading={true} />;
  }

  if (completionContext) {
    return (
      <CourseCompletionScreen
        courseId={courseId}
        courseTitle={currentCourse?.title || "Course"}
        enrollmentId={completionContext.enrollmentId}
        userId={completionContext.userId}
        userName={completionContext.userName}
        assessmentScore={completionContext.assessmentScore}
        onComplete={() => navigate(`/courses/${courseId}`, { replace: true })}
      />
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={3}>
        <Box mb={3} display="flex" alignItems="center" gap={2}>
          <IconButton onClick={handleBack} title="Go back">
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {assessmentID ? (isReadOnly ? "View Assessment" : "Edit Assessment") : "Create Assessment"}
            </Typography>
            <Box mt={0.5} display="flex" gap={1} flexWrap="wrap">
              <Chip size="small" color="info" variant="outlined" label="Step 3 of 3" />
              {isReadOnly ? (
                <Chip size="small" color="secondary" variant="outlined" icon={<Lock />} label="Read-only mode" />
              ) : null}
            </Box>
          </Box>
        </Box>

        <Formik
          initialValues={{
            title: assessment?.title || "",
            description: assessment?.description || "",
            moduleID: assessment?.moduleID || null,
            passingScore: assessment?.passingScore || 70,
            timeLimit: assessment?.timeLimit || 0,
            unlocksModuleID: assessment?.unlocksModuleID || null,
          }}
          validationSchema={Yup.object({
            title: Yup.string().required("Title is required"),
            passingScore: Yup.number().min(0).max(100).required("Passing score is required"),
          })}
          onSubmit={handleSaveAssessment}
          enableReinitialize
        >
          {({ values, setFieldValue }) => {
            const selectedModuleIndex = availableModules.findIndex(
              (module, index) => getModuleValue(module, index) === values.moduleID
            );
            const nextModule = selectedModuleIndex >= 0 ? availableModules[selectedModuleIndex + 1] : null;
            const unlockOptions = [
              { value: "", label: "None" },
              ...(nextModule
                ? [{ value: getModuleValue(nextModule, selectedModuleIndex + 1), label: nextModule.title }]
                : []),
            ];

            return (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormikInput name="title" label="Assessment Title" placeholder="Enter assessment title" size="large" disabled={isReadOnly} />
                  <Typography variant="caption" color="text" mt={0.5} display="block">
                    Use a clear title so instructors and learners can identify this assessment quickly.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikInput
                    name="passingScore"
                    type="number"
                    label="Passing Score (%)"
                    placeholder="70"
                    size="large"
                    disabled={isReadOnly}
                  />
                  <Typography variant="caption" color="text" mt={0.5} display="block">
                    Learners must score at least this percentage to pass.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <FormikInput
                    name="description"
                    label="Description"
                    placeholder="Enter assessment description"
                    multiline
                    rows={3}
                    size="large"
                    disabled={isReadOnly}
                  />
                  <Typography variant="caption" color="text" mt={0.5} display="block">
                    Add context or instructions students should read before starting.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormikInput
                    name="timeLimit"
                    type="number"
                    label="Time Limit (minutes)"
                    placeholder="0 (no limit)"
                    size="large"
                    disabled={isReadOnly}
                  />
                  <Typography variant="caption" color="text" mt={0.5} display="block">
                    Set to 0 for no time limit, or enter allowed minutes for completion.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormikInput
                    name="moduleID"
                    type="dropdown"
                    label="Assessment Belongs To (Optional)"
                    options={[
                      ...availableModules.map((m, i) => ({
                        value: getModuleValue(m, i),
                        label: m.title,
                      })),
                    ]}
                    size="large"
                    disabled={isReadOnly || availableModules.length === 0}
                  />
                  <Typography variant="caption" color="text" mt={0.5} display="block">
                    Select the module this assessment evaluates.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormikInput
                    name="unlocksModuleID"
                    type="dropdown"
                    label="Passing This Unlocks (Optional)"
                    options={unlockOptions}
                    size="large"
                    disabled={isReadOnly || availableModules.length === 0 || !values.moduleID}
                  />
                  <Typography variant="caption" color="text" mt={0.5} display="block">
                    Only the immediate next module can be unlocked from this assessment.
                  </Typography>
                </Grid>
                {availableModules.length === 0 && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="warning" display="block">
                      No modules found for this course yet. Add modules in Step 2 to enable module linking.
                    </Typography>
                  </Grid>
                )}

                {/* Questions Section */}
                <Grid item xs={12}>
                  <Box mt={4} mb={3} p={3} sx={{ border: '2px solid', borderColor: 'divider', borderRadius: 2, backgroundColor: 'background.paper' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h5" fontWeight="bold" color="text">
                        Assessment Questions
                      </Typography>
                      {!isReadOnly ? (
                        <Button 
                          variant="gradient" 
                          color="info" 
                          size="large" 
                          onClick={handleAddQuestion}
                          sx={{ 
                            minWidth: '180px', 
                            fontWeight: 'bold',
                            fontSize: '16px',
                            py: 1.5,
                            px: 3
                          }}
                        >
                          <Add sx={{ mr: 1, fontSize: '22px' }} /> Add Question
                        </Button>
                      ) : (
                        <Chip icon={<Visibility />} label="Viewing saved questions" color="info" variant="outlined" />
                      )}
                    </Box>
                    <Typography variant="body2" color="text" mb={2}>
                      Add multiple choice questions (MCQ) to your assessment. Each question can have multiple options with one correct answer.
                    </Typography>
                  </Box>

                  {!assessment || assessment.questions?.length === 0 ? (
                    <Box textAlign="center" py={4}>
                      <Typography variant="body1" color="text" mb={2}>
                        No questions yet. Add your first question to get started.
                      </Typography>
                      {!isReadOnly ? (
                        <Button variant="gradient" color="info" size="medium" onClick={handleAddQuestion}>
                          <Add sx={{ mr: 1 }} /> Add First Question
                        </Button>
                      ) : null}
                    </Box>
                  ) : (
                    <Box>
                      {assessment.questions?.map((question, index) => (
                        <Card key={index} sx={{ mb: 2 }}>
                          <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                              <Box>
                                <Typography variant="h6">
                                  Question {index + 1}: {question.question}
                                </Typography>
                                <Box display="flex" gap={1} mt={1}>
                                  <Chip label={`${question.points} points`} size="small" color="primary" variant="outlined" />
                                  <Chip label={`${question.options?.length} options`} size="small" color="secondary" variant="outlined" />
                                </Box>
                              </Box>
                              {!isReadOnly ? (
                                <Box display="flex" gap={1}>
                                  <IconButton size="small" onClick={() => handleEditQuestion(index)}>
                                    <Edit fontSize="small" />
                                  </IconButton>
                                  <IconButton size="small" onClick={() => handleDeleteQuestion(index)}>
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Box>
                              ) : null}
                            </Box>

                            <Box>
                              <Typography variant="subtitle2" mb={1}>
                                Options:
                              </Typography>
                              {question.options.map((option, optIndex) => (
                                <Box
                                  key={optIndex}
                                  display="flex"
                                  alignItems="center"
                                  gap={1}
                                  p={1}
                                  mb={0.5}
                                  sx={{
                                    backgroundColor: option.isCorrect ? "success.light" : "background.default",
                                    borderRadius: 1,
                                  }}
                                >
                                  <Typography variant="body2">{option.optionText}</Typography>
                                  {option.isCorrect && <Chip label="Correct" size="small" color="success" />}
                                </Box>
                              ))}
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                    <Button
                      type="button"
                      variant="outlined"
                      color="secondary"
                      onClick={handleBack}
                      disabled={loading}
                      startIcon={<ArrowBack />}
                    >
                      Back to Course Builder
                    </Button>
                    <Box display="flex" gap={2}>
                      <Button
                        type="button"
                        variant="outlined"
                        color="secondary"
                        onClick={() => navigate(`/courses/${courseId}`)}
                        disabled={loading}
                      >
                        Cancel
                      </Button>
                      {isReadOnly ? (
                        <Button
                          type="button"
                          variant="gradient"
                          color="info"
                          onClick={() => navigate(`/courses/${courseId}/assessments/${assessmentID}`)}
                        >
                          <Edit sx={{ mr: 1 }} /> Switch to Edit
                        </Button>
                      ) : (
                        <Button type="submit" variant="gradient" color="info" loading={loading} disabled={loading}>
                          <Save sx={{ mr: 1 }} /> {assessmentID ? "Update Assessment" : "Create Assessment"}
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          );
          }}
        </Formik>
      </Box>
      <Footer />

      {/* Question Dialog */}
      <QuestionDialog
        open={openQuestionDialog}
        onClose={() => {
          setOpenQuestionDialog(false);
          setEditingQuestion(null);
          setEditingQuestionIndex(null);
        }}
        onSave={handleSaveQuestion}
        question={editingQuestion}
      />
    </DashboardLayout>
  );
}

// Question Dialog Component
function QuestionDialog({
  open,
  onClose,
  onSave,
  question,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (data: Partial<Question>) => void;
  question: Question | null;
}) {
  const [options, setOptions] = useState<Option[]>(question?.options || []);

  useEffect(() => {
    setOptions(question?.options || []);
  }, [question]);

  const handleAddOption = () => {
    setOptions([...options, { optionText: "", isCorrect: false, order: options?.length }]);
  };

  const handleUpdateOption = (index: number, updates: Partial<Option>) => {
    const updated = [...options];
    updated[index] = { ...updated[index], ...updates };
    setOptions(updated);
  };

  const handleDeleteOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index).map((opt, i) => ({ ...opt, order: i })));
  };

  const handleSave = (values: any) => {
    if (!values.question || values.question.trim() === "") {
      alert("Please enter a question");
      return;
    }

    if (options.length < 2) {
      alert("Please add at least 2 options for this question");
      return;
    }

    // Check if all options have text
    const hasEmptyOptions = options.some((opt) => !opt.optionText || opt.optionText.trim() === "");
    if (hasEmptyOptions) {
      alert("Please fill in all option texts");
      return;
    }

    const hasCorrect = options.some((opt) => opt.isCorrect);
    if (!hasCorrect) {
      alert("Please mark at least one option as the correct answer");
      return;
    }

    onSave({ ...values, options });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{question ? "Edit Question" : "Add Question"}</DialogTitle>
      <DialogContent>
        <Formik
          initialValues={{
            question: question?.question || "",
            points: question?.points || 1,
          }}
          onSubmit={handleSave}
        >
          {({ submitForm, values, setFieldValue }) => (
            <Form>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <FormikInput
                    name="question"
                    label="Question Text *"
                    placeholder="Enter your question here..."
                    multiline
                    rows={3}
                    size="large"
                  />
                  <Typography variant="caption" color="text" mt={0.5} display="block">
                    Enter the question that students will see
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikInput 
                    name="points" 
                    type="number" 
                    label="Points *" 
                    placeholder="1" 
                    size="large"
                  />
                  <Typography variant="caption" color="text" mt={0.5} display="block">
                    Points awarded for correct answer
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2">Options</Typography>
                    <Button variant="outlined" color="info" size="small" onClick={handleAddOption}>
                      <Add sx={{ mr: 0.5, fontSize: 16 }} /> Add Option
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text" mt={0.5} mb={1.5} display="block">
                    Use the &quot;Correct&quot; checkbox to mark right answers. At least one option must be marked correct.
                  </Typography>
                  {options.length === 0 ? (
                    <Box textAlign="center" py={2}>
                      <Typography variant="body2" color="text" mb={2}>
                        No options yet. Add at least 2 options for this question.
                      </Typography>
                      <Button variant="outlined" color="info" size="small" onClick={handleAddOption}>
                        <Add sx={{ mr: 0.5 }} /> Add First Option
                      </Button>
                    </Box>
                  ) : (
                    options.map((option, index) => (
                      <Card key={index} variant="outlined" sx={{ mb: 1.5, p: 1.5 }}>
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          <Typography variant="caption" sx={{ mb: 0.5 }}>{`Option ${index + 1} *`}</Typography>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Box flex={1}>
                              <Input
                                {...({
                                  placeholder: "Enter option text",
                                  value: option.optionText,
                                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleUpdateOption(index, { optionText: e.target.value }),
                                  size: "small",
                                  required: true
                                } as any)}
                              />
                            </Box>
                            <Box display="flex" alignItems="center" ml={1}>
                              <Checkbox
                                checked={option.isCorrect}
                                onChange={(e) => handleUpdateOption(index, { isCorrect: e.target.checked })}
                                color="primary"
                                sx={{ p: 0.5 }}
                              />
                              <Typography variant="body2" sx={{ ml: 0.5 }}>
                                Correct
                              </Typography>
                            </Box>
                            <IconButton 
                              size="small" 
                              onClick={() => handleDeleteOption(index)}
                              color="error"
                              disabled={options.length <= 2}
                              title={options.length <= 2 ? "At least 2 options required" : "Delete option"}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Card>
                    ))
                  )}
                </Grid>
              </Grid>
              <DialogActions sx={{ mt: 2 }}>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={submitForm} variant="gradient" color="info">
                  Save
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}

export default AssessmentBuilder;

